import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, HelpCircle, Code, FileText, MessageCircle, 
  ExternalLink, Search, ArrowRight, Mail, Newspaper
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';

const resources = [
  {
    icon: HelpCircle,
    title: 'Centre d\'aide',
    description: 'Trouvez des réponses à toutes vos questions fréquentes sur l\'utilisation de SENDBOX.',
    link: '#help',
    color: 'blue'
  },
  {
    icon: BookOpen,
    title: 'Guide de démarrage',
    description: 'Apprenez à utiliser SENDBOX en quelques minutes avec notre guide complet.',
    link: '#guide',
    color: 'green'
  },
  {
    icon: Code,
    title: 'API Développeurs',
    description: 'Intégrez SENDBOX dans vos applications avec notre API simple et puissante.',
    link: '#api',
    color: 'purple'
  },
  {
    icon: FileText,
    title: 'Documentation',
    description: 'Documentation technique complète pour les utilisateurs avancés.',
    link: '#docs',
    color: 'cyan'
  }
];

const faqs = [
  {
    question: 'Quelle est la taille maximum des fichiers ?',
    answer: 'Vous pouvez envoyer des fichiers jusqu\'à 20 Go avec SENDBOX, que vous soyez en version gratuite ou Pro.'
  },
  {
    question: 'Combien de temps les liens restent-ils actifs ?',
    answer: 'En version gratuite, les liens expirent après 7 jours. Les utilisateurs Pro peuvent personnaliser la durée d\'expiration.'
  },
  {
    question: 'Mes fichiers sont-ils sécurisés ?',
    answer: 'Oui, tous les fichiers sont chiffrés en transit. Les liens expirent automatiquement et les fichiers sont supprimés après expiration.'
  },
  {
    question: 'Dois-je créer un compte pour envoyer des fichiers ?',
    answer: 'Non, vous pouvez envoyer des fichiers sans compte. Un compte vous permet de suivre vos envois et accéder aux fonctionnalités Pro.'
  },
  {
    question: 'Puis-je annuler mon abonnement Pro ?',
    answer: 'Oui, vous pouvez annuler votre abonnement à tout moment. Vous conserverez l\'accès Pro jusqu\'à la fin de la période payée.'
  },
  {
    question: 'Comment fonctionne le fond personnalisé Pro ?',
    answer: 'Les utilisateurs Pro peuvent uploader une image ou vidéo qui apparaîtra en fond sur la page de téléchargement de leurs destinataires.'
  }
];

const blogPosts = [
  {
    title: 'Comment partager des fichiers volumineux en toute sécurité',
    excerpt: 'Découvrez les meilleures pratiques pour partager vos fichiers en préservant leur confidentialité.',
    date: '15 Fév 2026'
  },
  {
    title: 'Les 5 erreurs à éviter lors du partage de fichiers',
    excerpt: 'Évitez ces pièges courants pour garantir que vos fichiers arrivent à destination.',
    date: '10 Fév 2026'
  },
  {
    title: 'Nouveau : Statistiques de téléchargement pour les Pro',
    excerpt: 'Suivez en temps réel qui télécharge vos fichiers avec notre nouvelle fonctionnalité.',
    date: '5 Fév 2026'
  }
];

const ResourcesPage = () => {
  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500/20 text-blue-400',
      green: 'bg-green-500/20 text-green-400',
      purple: 'bg-purple-500/20 text-purple-400',
      cyan: 'bg-cyan-500/20 text-cyan-400'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-300 text-sm mb-6">
            <BookOpen className="w-4 h-4" />
            Centre de ressources
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Tout ce qu'il vous faut<br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              pour bien démarrer
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">
            Guides, documentation et FAQ pour tirer le meilleur parti de SENDBOX.
          </p>
        </section>

        {/* Resources Grid */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <a
                  key={index}
                  href={resource.link}
                  className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/70 hover:border-blue-500/30 transition-all group block"
                >
                  <div className={`w-12 h-12 rounded-xl ${getColorClasses(resource.color)} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-300 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-blue-200/60 text-sm">{resource.description}</p>
                </a>
              );
            })}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Questions fréquentes
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-6"
              >
                <h3 className="text-lg font-medium text-white mb-3 flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-blue-200/70 pl-8">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Blog Section */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Newspaper className="w-8 h-8 text-blue-400" />
              Derniers articles
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.map((post, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:bg-slate-800/70 transition-all cursor-pointer group"
              >
                <p className="text-blue-400 text-sm mb-3">{post.date}</p>
                <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors">
                  {post.title}
                </h3>
                <p className="text-blue-200/60 text-sm mb-4">{post.excerpt}</p>
                <span className="text-blue-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  Lire la suite <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-3xl p-8 md:p-12 text-center">
            <MessageCircle className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Besoin d'aide ?
            </h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">
              Notre équipe est là pour vous aider. Contactez-nous par email et nous vous 
              répondrons dans les plus brefs délais.
            </p>
            <Button className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600">
              <Mail className="w-5 h-5 mr-2" />
              contact@sendbox.fr
            </Button>
          </div>
        </section>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default ResourcesPage;
